import {assert} from 'Asserts';
import * as ast from 'notebook/tables/excess/AST';
import {ASTNode, CellLock, RangeLock} from 'notebook/tables/excess/AST';
import * as util from 'notebook/tables/excess/Util';
import {CellIdWithOptionalTable, TypedValue} from 'notebook/tables/TableConstants';
import {Optional} from 'util/Types';

/**
 * This is a TypeScript implementation of the spreadsheet tokenizer.
 * The backend tokenizer is found in benchling/notebook/tables/excess/tokenizer.py
 * Please keep the two algorithms in sync by updating both the files and corresponding tests.
 */
export class Token {
  type: TYPES;
  text: string;
  node: ASTNode | null;

  constructor(type: TYPES, text: string, node: Optional<ASTNode> = null) {
    this.type = type;
    this.text = text;
    this.node = node;
    assert(
      (this.node != null) === (this.type === TYPES.AST_NODE),
      'Token has a node if and only if it is an ASTNode.'
    );
  }

  value(): ASTNode {
    assert(this.isASTNode(), 'Only AST node Tokens have a value.');
    return this.node;
  }

  isComma(): this is {type: TYPES.COMMA} {
    return this.type === TYPES.COMMA;
  }

  isLeftParen(): this is {type: TYPES.LEFT_PAREN} {
    return this.type === TYPES.LEFT_PAREN;
  }

  isRightParen(): this is {type: TYPES.RIGHT_PAREN} {
    return this.type === TYPES.RIGHT_PAREN;
  }

  isSpace(): this is {type: TYPES.SPACE} {
    return this.type === TYPES.SPACE;
  }

  isASTNode(): this is {type: TYPES.AST_NODE; node: ASTNode} {
    return this.type === TYPES.AST_NODE;
  }

  isCellRef(): this is {type: TYPES.AST_NODE; node: ast.Cell | ast.CellRange} {
    return this.isASTNode() && (this.node instanceof ast.Cell || this.node instanceof ast.CellRange);
  }
}

function parseRow(row: string): number {
  return parseInt(row, 10) - 1;
}

function parseCol(col: string): number {
  return util.colLettersToNumbers(col);
}

/**
 * Returns the table name corresponding to a match.
 * @param  {Array} match   Array of strings returned by the `exec()` method of a regular expression beginning
 *                         with SHEET_NAME_PATTERN.
 * @return {String}        Table name correponding to match, or null if no table name was found.
 */
function getTableName(match: RegExpExecArray): string | null {
  const unescapedTableName = match[1];
  const escapedTableName = match[2] != null ? match[2].replace(/''/g, "'") : undefined;
  return unescapedTableName || escapedTableName || null;
}

export interface Anchor {
  row: number;
  column: number;
}

export enum TYPES {
  LEFT_PAREN = 'LEFT_PAREN',
  RIGHT_PAREN = 'RIGHT_PAREN',
  COMMA = 'COMMA',
  SPACE = 'SPACE',
  AST_NODE = 'AST_NODE',
}

const SHEET_NAME_PATTERN =
  '(?:' + // capturing group for sheet name
  '(?:((?!!)\\w+)!)' + // sheet name can be just letters followed by ! (e.g sheet1!)
  '|' +
  "(?:'((?:''|[^'])+)'!)" /* sheet name can be surrounded by single quotes - single quotes are
  escaped by a single quote ('sheet1'!, 'shee''t1'!, 'sh''ee''t1'!) */ +
  ')'; // sheet name isn't required

const COLUMN_ROW_PATTERN =
  '(\\$?)' + // capturing group for column lock
  '([A-Za-z]+)' + // capturing group for column
  '(\\$?)' + // capturing group for row lock
  '(\\d+)'; // capturing group for row

const REL_COLUMN_ROW_PATTERN = `\
R(?:(-?\\d+)|\\[(-?\\d+)\\])\
C(?:(-?\\d+)|\\[(-?\\d+)\\])\
`;

const CELL_REGEX = new RegExp(`^${SHEET_NAME_PATTERN}?${COLUMN_ROW_PATTERN}`);
const RANGE_REGEX = new RegExp(`^${SHEET_NAME_PATTERN}?${COLUMN_ROW_PATTERN}:${COLUMN_ROW_PATTERN}`);

const REL_CELL_REGEX = new RegExp(`^${SHEET_NAME_PATTERN}?${REL_COLUMN_ROW_PATTERN}`);
const REL_RANGE_REGEX = new RegExp(
  `^${SHEET_NAME_PATTERN}?${REL_COLUMN_ROW_PATTERN}:${REL_COLUMN_ROW_PATTERN}`
);

function tokenize(string: string, param?: {relative?: boolean}): Array<Token> {
  if (param == null) {
    param = {};
  }
  let {relative} = param;
  if (relative == null) {
    relative = false;
  }

  const tokens: Array<Token> = [];

  while (string.length > 0) {
    let endId: CellIdWithOptionalTable;
    let id: CellIdWithOptionalTable;
    let locks: Partial<RangeLock> & Partial<CellLock>;
    let node: ast.ASTNode;
    let startId: CellIdWithOptionalTable;
    let table: string | null;
    let token: Token;
    let tokenText: string;
    let value: TypedValue;

    const leftParen = /^\(/.exec(string);
    if (leftParen) {
      tokenText = leftParen[0];
      token = new Token(TYPES.LEFT_PAREN, tokenText);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    const rightParen = /^\)/.exec(string);
    if (rightParen) {
      tokenText = rightParen[0];
      token = new Token(TYPES.RIGHT_PAREN, tokenText);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    const comma = /^,/.exec(string);
    if (comma) {
      tokenText = comma[0];
      token = new Token(TYPES.COMMA, tokenText);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    const space = /^\s+/.exec(string);
    if (space) {
      tokenText = space[0];
      token = new Token(TYPES.SPACE, tokenText);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    const func = /^(\w+)\(/.exec(string);
    if (func) {
      tokenText = func[1]; // We want the parenthesis to be the next token
      // @ts-expect-error
      node = new ast.FunctionOperator({type: tokenText.toUpperCase()});
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    const operator = /^((<>)|(>=)|(<=)|[+\-/*^><=])/.exec(string);
    if (operator) {
      tokenText = operator[0];
      node = new ast.BinaryOperator({type: tokenText as ast.BinaryOperatorType});
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // Relative cell range (must be parsed before relative cell)
    const relativeCellRange = REL_RANGE_REGEX.exec(string);
    if (relative && relativeCellRange) {
      table = getTableName(relativeCellRange);
      startId = {
        row: parseInt(relativeCellRange[3] || relativeCellRange[4], 10),
        column: parseInt(relativeCellRange[5] || relativeCellRange[6], 10),
        table,
      };
      endId = {
        row: parseInt(relativeCellRange[7] || relativeCellRange[8], 10),
        column: parseInt(relativeCellRange[9] || relativeCellRange[10], 10),
        table,
      };
      locks = {
        startRow: relativeCellRange[3] != null,
        startCol: relativeCellRange[5] != null,
        endRow: relativeCellRange[7] != null,
        endCol: relativeCellRange[9] != null,
      };

      tokenText = relativeCellRange[0];
      node = new ast.RelCellRange({start: startId, end: endId, text: tokenText, locks});
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // Relative cell reference (must be parsed before single cell)
    const relativeCellReference = REL_CELL_REGEX.exec(string);
    if (relative && relativeCellReference) {
      locks = {
        row: relativeCellReference[3] != null,
        col: relativeCellReference[5] != null,
      };
      id = {
        row: parseInt(relativeCellReference[3] || relativeCellReference[4], 10),
        column: parseInt(relativeCellReference[5] || relativeCellReference[6], 10),
        table: getTableName(relativeCellReference),
      };

      tokenText = relativeCellReference[0];
      node = new ast.RelCell({id, text: tokenText, locks});
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // Range of cells (must be parsed before single cell)
    const rangeOfCells = RANGE_REGEX.exec(string);
    if (rangeOfCells) {
      table = getTableName(rangeOfCells);
      startId = {
        table,
        row: parseRow(rangeOfCells[6]),
        column: parseCol(rangeOfCells[4]),
      };
      endId = {table, row: parseRow(rangeOfCells[10]), column: parseCol(rangeOfCells[8])};
      locks = {
        startCol: rangeOfCells[3] === '$',
        startRow: rangeOfCells[5] === '$',
        endCol: rangeOfCells[7] === '$',
        endRow: rangeOfCells[9] === '$',
      };

      tokenText = rangeOfCells[0];
      node = new ast.CellRange({start: startId, end: endId, text: tokenText, locks});
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // Single cell
    const singleCell = CELL_REGEX.exec(string);
    if (singleCell) {
      id = {
        row: parseRow(singleCell[6]),
        column: parseCol(singleCell[4]),
        table: getTableName(singleCell),
      };
      const cellLocks: ast.CellLock = {
        col: singleCell[3] === '$',
        row: singleCell[5] === '$',
      };
      tokenText = singleCell[0];
      node = new ast.Cell({id, text: tokenText, locks: cellLocks});
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // Number
    const numRegEx = new RegExp(`^${util.NUMBER_REGEX}`).exec(string);
    if (numRegEx) {
      value = util.parseValue(numRegEx[0], numRegEx, null);
      tokenText = numRegEx[0];
      node = new ast.Value(value);
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // Boolean
    const booleanRegEx = new RegExp(`^${util.BOOLEAN_REGEX.source}`, 'i').exec(string);
    if (booleanRegEx) {
      value = util.parseValue(booleanRegEx[0], null, null, booleanRegEx);
      tokenText = booleanRegEx[0];
      node = new ast.Value(value);
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // String
    const stringRegEx = new RegExp(`^${util.STRING_REGEX}`).exec(string);
    if (stringRegEx) {
      // Replace escaped double quotes with the double quote character.
      const doubleQuoteStr = stringRegEx[1].replace(new RegExp(util.ESCAPED_QUOTE_REGEX, 'g'), '"');
      node = new ast.Value(util.parseValue(doubleQuoteStr, null, stringRegEx));
      tokenText = stringRegEx[0];
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // Reference Error
    const referenceError = /^#REF!/.exec(string);
    if (referenceError) {
      node = new ast.ReferenceErrorNode();
      tokenText = referenceError[0];
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    throw new util.FormulaTokenizeError(`Found unexpected characters: ${string}`, {
      tokens,
      leftover: string,
    });
  }

  return tokens;
}

function partialTokenize(
  formula: string,
  options: {relative: boolean}
): {
  tokens: Array<Token> | undefined;
  leftover: string | undefined;
} {
  let leftover: string | undefined;
  let tokens: Array<Token> | undefined;
  try {
    tokens = tokenize(formula, options);
    leftover = '';
  } catch (e) {
    if (e instanceof util.FormulaTokenizeError) {
      ({tokens} = e);
      ({leftover} = e);
    } else {
      throw e;
    }
  }
  return {tokens, leftover};
}

// Takes an absolute formula and converts it to a formula relative to the specified cell
function toRelativeFormula(absFormula: string, cell: CellIdWithOptionalTable): string {
  const absFormulaText = util.getFormulaText(absFormula);
  if (absFormulaText == null) {
    return absFormula;
  }

  // Convert tokens to relative formula, and append leftover.
  const {tokens, leftover} = partialTokenize(absFormulaText, {relative: false});
  const relTokens: Array<Token> = [];
  // @ts-expect-error
  tokens.forEach((token) => {
    if (token.isASTNode() && token.node instanceof ast.AbsoluteCellReference) {
      const node = token.node.toRelativeNode(cell);
      relTokens.push(new Token(TYPES.AST_NODE, node.text, node));
    } else {
      relTokens.push(token);
    }
  });

  return `=${relTokens.map((token) => token.text).join('')}${leftover}`;
}

// Takes a formula relative to the specified cell and converts it to an absolute formula
function toAbsoluteFormula(relFormula: string, cell: CellIdWithOptionalTable): string {
  const relFormulaText = util.getFormulaText(relFormula);
  if (relFormulaText == null) {
    return relFormula;
  }

  const {tokens, leftover} = partialTokenize(relFormulaText, {relative: true});

  const absTokens: Array<Token> = [];
  // @ts-expect-error
  tokens.forEach((token) => {
    if (token.isASTNode() && token.node instanceof ast.RelativeCellReference) {
      const node = token.node.toAbsoluteNode(cell);
      absTokens.push(new Token(TYPES.AST_NODE, node.text, node));
    } else {
      absTokens.push(token);
    }
  });

  return `=${absTokens.map((token) => token.text).join('')}${leftover}`;
}

export default {
  tokenize,
  partialTokenize,
  getTableName,
  toRelativeFormula,
  toAbsoluteFormula,
};
